package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.domain.model.NotificationCategory
import com.evoapps.evoshift.domain.model.NotificationSettings
import com.evoapps.evoshift.domain.repository.NotificationSettingsRepository
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.serialization.Serializable

/**
 * `users/{uid}/settings/notifications` (SH-73): one switch per [NotificationCategory] name, read by
 * functions/notificationPrefs.js before each push. A missing switch is on. SH-241: [email] holds
 * the e-mail switches, [TRIAL_TIPS] for the trial e-mails (functions/trialEmails.js reads it, and
 * its unsubscribe link sets it to false).
 */
@Serializable
internal data class NotificationSettingsDto(
    val push: Map<String, Boolean> = emptyMap(),
    val email: Map<String, Boolean> = emptyMap(),
    val updatedAt: String? = null
)

internal const val TRIAL_TIPS = "TRIAL_TIPS"

internal fun NotificationSettingsDto.toDomain() = NotificationSettings(
    pushOff = NotificationCategory.entries.filter { push[it.name] == false }.toSet(),
    trialEmails = email[TRIAL_TIPS] != false
)

internal fun NotificationSettings.toDto(updatedAt: String) = NotificationSettingsDto(
    push = NotificationCategory.entries.associate { it.name to isOn(it) },
    email = mapOf(TRIAL_TIPS to trialEmails),
    updatedAt = updatedAt
)

class NotificationSettingsRepositoryImpl(private val firestore: AppFirestore) : NotificationSettingsRepository {
    private fun document(userId: String) =
        firestore.collection("users").document(userId).collection("settings").document("notifications")

    override fun observe(userId: String): Flow<NotificationSettings> =
        document(userId).safeSnapshots
            .map { snapshot ->
                if (!snapshot.exists) NotificationSettings()
                else runCatching { snapshot.data<NotificationSettingsDto>().toDomain() }.getOrDefault(NotificationSettings())
            }
            .distinctUntilChanged()

    override suspend fun save(userId: String, settings: NotificationSettings): Result<Unit> = runCatching {
        document(userId).set(settings.toDto(AppClock.now().toString()))
    }
}
